import { Big } from 'big.js'
import { Measure } from '../types'

enum SpeedMeasurementSystem {
    metric = 'Metric Units',
    imperial = 'Imperial Units',
    nautical = 'Nautical Units',
    astronomical = 'Astronomical Units'
}

// synonym: velocity
const speedUnits = {
  meter_per_second: {
    displayName: 'Meter per Second',
    codeName: 'meter_per_second',
    url: 'meter-per-second',
    symbol: 'm/s',
    toBaseUnit: (value: Big): Big => value,
    fromBaseUnit: (value: Big): Big => value,
    system: SpeedMeasurementSystem.metric
  },
  kilometer_per_second: {
    displayName: 'Kilometer per Second',
    codeName: 'kilometer_per_second',
    url: 'kilometer-per-second',
    symbol: 'km/h',
    toBaseUnit: (value: Big): Big => value.times(1000),
    fromBaseUnit: (value: Big): Big => value.div(1000),
    system: SpeedMeasurementSystem.metric
  },
  meter_per_hour: {
    displayName: 'Meter per Hour',
    codeName: 'meter_per_hour',
    url: 'meter-per-hour',
    symbol: 'm/s',
    toBaseUnit: (value: Big): Big => value.div(3600),
    fromBaseUnit: (value: Big): Big => value.times(3600),
    system: SpeedMeasurementSystem.metric
  },
  kilometer_per_hour: {
    displayName: 'Kilometer per Hour',
    codeName: 'kilometer_per_hour',
    url: 'kilometer-per-hour',
    symbol: 'km/h',
    toBaseUnit: (value: Big): Big => value.div(3.6),
    fromBaseUnit: (value: Big): Big => value.times(3.6),
    system: SpeedMeasurementSystem.metric
  },

  foot_per_second: {
    displayName: 'Foot per Second',
    codeName: 'foot_per_second',
    url: 'foot-per-second',
    symbol: 'ft/s',
    toBaseUnit: (value: Big): Big => value.times(0.3048),
    fromBaseUnit: (value: Big): Big => value.div(0.3048),
    system: SpeedMeasurementSystem.imperial
  },
  mile_per_second: {
    displayName: 'Mile per Second',
    codeName: 'mile_per_second',
    url: 'mile-per-second',
    symbol: 'mph',
    toBaseUnit: (value: Big): Big => value.times(1609.344),
    fromBaseUnit: (value: Big): Big => value.div(1609.344),
    system: SpeedMeasurementSystem.imperial
  },
  foot_per_hour: {
    displayName: 'Foot per Hour',
    codeName: 'foot_per_hour',
    url: 'foot-per-hour',
    symbol: 'ft/s',
    toBaseUnit: (value: Big): Big => value.div(11811.023622),
    fromBaseUnit: (value: Big): Big => value.times(11811.023622),
    system: SpeedMeasurementSystem.imperial
  },
  mile_per_hour: {
    displayName: 'Mile per Hour',
    codeName: 'mile_per_hour',
    url: 'mile-per-hour',
    symbol: 'mph',
    toBaseUnit: (value: Big): Big => value.times(0.44704),
    fromBaseUnit: (value: Big): Big => value.div(0.44704),
    system: SpeedMeasurementSystem.imperial
  },

  knot: {
    displayName: 'Knot',
    codeName: 'knot',
    url: 'knot',
    symbol: 'kn',
    toBaseUnit: (value: Big): Big => value.div(1.9438444924),
    fromBaseUnit: (value: Big): Big => value.times(1.9438444924),
    system: SpeedMeasurementSystem.nautical
  },

  speed_of_light: {
    displayName: 'Speed of Light',
    codeName: 'speed_of_light',
    url: 'speed-of-light',
    symbol: 'c',
    toBaseUnit: (value: Big): Big => value.times(299792458),
    fromBaseUnit: (value: Big): Big => value.div(299792458),
    system: SpeedMeasurementSystem.astronomical
  }
}

export const speed: Measure = {
  displayName: 'Speed',
  codeName: 'speed',
  url: 'speed',
  units: speedUnits,
  baseUnit: speedUnits.meter_per_second,
  defaultFromUnit: speedUnits.meter_per_second,
  defaultToUnit: speedUnits.kilometer_per_hour
}
